/**
 * Main Application Logic
 * This file contains all the heavy application initialization logic
 * and will be loaded as a separate chunk by the bootstrap
 */

import Focusable from '../utils/focusable';
import {
  isHomePage,
  runOnAllButProd,
  runOnAllDomains,
  runOnDevTestStageProdDomains,
  runOnStageProdDomain,
  runOnTestStageProdDomains,
} from '../utils/aem-utils';
// Video components
import { removeNoJsClassFromHTMLElement } from '../utils/dom-utils';
import { init as initGalleryScrollingTracking } from '../components/photogallery/gallery-scrolling-fluent';
import { initDataLayerAttributes, initDatalayerElement } from '../components/tracking/datalayer';
import { runAsyncFuncs } from '../utils/function-utils';
import { initActionsHistory } from '../global-store';
import { clearParselyTrackingCookies, initParselyTrackingService } from '../services/tracking/parsely-tracking-service';
import { initLoggingService } from '../services/logging/logging';

import {
  asyncMultipleVideoPlaylist,
  asyncVideoArticle,
  asyncVideoPlayers,
  asyncVideoPlaylistInline,
  asyncWebviewVideoPlayer,
} from './async-importer/video-components';
// Navigation components
import {
  asyncGlobalNavigation,
  asyncLocalNavigation,
  asyncNavCustom,
  asyncSectionNavigation,
} from './async-importer/navigation-components';
// Analytics components
import {
  asyncAnalyticsFrom,
  asyncAudioTagTracking,
  asyncParselyMyLocalNews,
  asyncParselyMySport,
  asyncParselyMyTeam,
  asyncParselyRender,
  asyncParselyRenderRailMostViewed,
} from './async-importer/analytics-components';
// Advertising components
import {
  asyncAdvArticle,
  asyncAdvIntro,
  asyncAdvSkin,
  asyncAdvTopBottom,
} from './async-importer/advertising-components';
// Auth components
import {
  asyncAuth,
  asyncLoginOnGlobalNav,
  asyncPaywall,
  asyncPremiumPricingTable,
  asyncUserPreferences,
} from './async-importer/auth-components';
// Newsletter components
import { asyncBannerNewsletter, asyncNewsletterSignup } from './async-importer/newsletter-components';
// UI utility components
import {
  asyncLandToElement,
  asyncPageRefresh,
  asyncReadingProgressBar,
  asyncScrollToTop,
  asyncTabSwitch,
  asyncSetOfCardsInfinite,
} from './async-importer/ux-utility-components';
// External components
import {
  asyncAutoPushNotifications,
  asyncEmbeds,
  asyncOutbrain,
  asyncSkySocialShare,
  asyncTwitterLib,
} from './async-importer/external-components';
// Sports components
import { asyncELStreaming, asyncNbaGlobal, asyncSectionEuroTvSchedule } from './async-importer/sports-components';
// News components
import {
  asyncLatestNews,
  asyncLatestNewsInfinite,
  asyncNewsYouCanUse,
  asyncOtherNewsChecker,
} from './async-importer/news-components';
// Misc components
import {
  asyncCarouselInline,
  asyncCheckFilters,
  asyncInfiniteScrolling,
  asyncInjectHomepage,
  asyncPathbreakerSlider,
  asyncPrimaryTag,
  asyncSectionDividerSpecial,
  asyncSignsAffinity,
  asyncSlimBannerBottomCTA,
  asyncSlimBannerBottomWPN,
  asyncTextToSpeech,
  asyncToolbarCalendar,
  asyncTvCardOnRailChangePosition,
  asyncTvCardSlim,
} from './async-importer/misc-components';
import { initLazyContentLoader } from './async-importer/lazy-content-loader';

const config = require('config');

/**
 * Register service worker on window.load
 */
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && config.serviceWorker.enabled) {
    navigator.serviceWorker
      .register(`/service-worker.js`, {
        scope: '/',
      })
      .then(() => {
        console.info(`service worker registered`);
      })
      .catch((registrationError) => {
        console.error(`service worker registration failed - registration error: ${registrationError}`);
      });
  } else {
    console.warn(
      ` 0. IsHomePage: ${isHomePage()} serviceWorker.register only on homepage.
        1. If you're in HomePage ensure that your service worker is enabled in
           config.serviceWorker.enabled: ${config.serviceWorker.enabled}
        2. Then ensure that your browser support the Service-Worker or serve your page over HTTPS.
        Service workers require a Secure Context!`
    );
  }
};

/**
 * Configuration for window load event handlers
 */
const onWindowLoadConfig = {
  allDomains: [
    registerServiceWorker,
    asyncParselyRender,
    asyncParselyRenderRailMostViewed,
    asyncInfiniteScrolling,
    asyncToolbarCalendar,
    asyncCheckFilters,
    asyncPageRefresh,
    asyncAutoPushNotifications,
    asyncTwitterLib,
  ],
  devTestStageProd: [],
  testStageProd: [],
  stageProd: [],
};

/**
 * Window load callback - all assets are loaded
 */
const onWindowLoad = () => {
  try {
    runOnAllDomains(() => runAsyncFuncs(onWindowLoadConfig.allDomains));
    runOnDevTestStageProdDomains(() => runAsyncFuncs(onWindowLoadConfig.devTestStageProd));
    runOnTestStageProdDomains(() => runAsyncFuncs(onWindowLoadConfig.testStageProd));
    runOnStageProdDomain(() => runAsyncFuncs(onWindowLoadConfig.stageProd));
  } catch (error) {
    console.warn('Error in onWindowLoad() : ', error);
  }
};

/**
 * Configuration for DOM content loaded event handlers
 */
const onDOMContentLoadedConfig = {
  allDomains: [
    asyncReadingProgressBar,
    asyncVideoPlayers,
    asyncMultipleVideoPlaylist,
    asyncWebviewVideoPlayer,
    asyncVideoArticle,
    initActionsHistory,
    asyncAuth,
    asyncLoginOnGlobalNav,
    asyncSetOfCardsInfinite,
    asyncTabSwitch,
    asyncGlobalNavigation,
    asyncLocalNavigation,
    asyncSectionNavigation,
    asyncPrimaryTag,
    asyncNavCustom,
    asyncNbaGlobal,
    asyncOtherNewsChecker,
    asyncAdvArticle,
    asyncAdvTopBottom,
    asyncAdvSkin,
    asyncAdvIntro,
    initDatalayerElement,
    asyncELStreaming,
    asyncTvCardOnRailChangePosition,
    asyncTvCardSlim,
    asyncSignsAffinity,
    asyncPathbreakerSlider,
    asyncLandToElement,
    asyncAnalyticsFrom,
    asyncAudioTagTracking,
    asyncSlimBannerBottomCTA,
    asyncSlimBannerBottomWPN,
    asyncNewsletterSignup,
    asyncParselyMyTeam,
    asyncParselyMySport,
    asyncParselyMyLocalNews,
    asyncLatestNews,
    asyncLatestNewsInfinite,
    asyncUserPreferences,
    asyncBannerNewsletter,
    asyncTextToSpeech,
    asyncVideoPlaylistInline,
    asyncCarouselInline,
    asyncSkySocialShare,
    asyncNewsYouCanUse,
    asyncSectionDividerSpecial,
    asyncSectionEuroTvSchedule,
    asyncPremiumPricingTable,
    asyncPaywall,
    asyncScrollToTop,
    setTimeout(() => initGalleryScrollingTracking(), 0),
    initLazyContentLoader(),
  ],
  devTestStageProd: [asyncInjectHomepage, asyncOutbrain],
  testStageProd: [asyncEmbeds],
  stageProd: [],
  allButProd: [],
};

/**
 * DOM content loaded callback - DOM is ready
 */
const onDOMContentLoaded = () => {
  /**
   * Identifying keyboard navigators and add 'user-is-tabbing' to body
   */
  window.addEventListener('keydown', Focusable.handleFirstTab);

  runOnAllDomains(() => runAsyncFuncs(onDOMContentLoadedConfig.allDomains));
  runOnDevTestStageProdDomains(() => runAsyncFuncs(onDOMContentLoadedConfig.devTestStageProd));
  runOnAllButProd(() => runAsyncFuncs(onDOMContentLoadedConfig.allButProd));
  runOnTestStageProdDomains(() => runAsyncFuncs(onDOMContentLoadedConfig.testStageProd));
  runOnStageProdDomain(() => runAsyncFuncs(onDOMContentLoadedConfig.stageProd));
};

/**
 * Main Application Start Function
 */
export const startClient = () => {
  // Initialize tracking as early as possible
  initLoggingService();
  initDataLayerAttributes();
  clearParselyTrackingCookies();
  initParselyTrackingService();

  // Remove no-js class
  removeNoJsClassFromHTMLElement();

  /**
   * Ensure onDOMContentLoaded fires even if the event has already been fired
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onDOMContentLoaded);
  } else {
    onDOMContentLoaded();
  }

  // Register window load event
  window.addEventListener('load', onWindowLoad);

  console.info('Main Application: Started successfully');
};
